import { LoveClientError } from "@emed-labs/platform-utility-generic-clients";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { PropsWithChildren, RefObject, createContext, useContext, useEffect, useRef, useState } from "react";
import {
	ExchangeCredentialRequest,
	ExchangeCredentialResponse,
	Patient,
	Person,
	ValidateLoginResponse
} from "../../../vlab-models";
import { useStateRef } from "../hooks/UseStateRef";
import { VLabRole } from "./SessionStateProvider";
import { VLabAPIContext } from "./VLabAPIProvider";
import { LogContext, useLogger } from "./LogProvider";

export interface AuthContext {
	/**
	 * User is authenticated
	 */
	authenticated: boolean;
	/**
	 * Authentication in progress
	 */
	authenticating: boolean;
	/**
	 * Returns longer lived token
	 * @param exchangeToken
	 * @returns
	 */
	loginWithExchangeToken: (exchangeToken: string) => Promise<string | undefined>;
	sessionID?: string;
	token?: string;
	tokenRef: RefObject<string | undefined>;
	lastExchangeTokenRef: RefObject<string | undefined>;
	role?: VLabRole;
	actor?: Person | Patient;
	patient?: Patient;
	logout: () => void;
	onFailureRedirectUri?: string;
	onSuccessRedirectUri?: string;
	setCredential: (cred: ExchangeCredentialResponse) => string;
}

/**
 * Hides UI and shows a spinner
 * @param props
 * @returns
 */
export const AuthContext = createContext<AuthContext>({} as AuthContext);
AuthContext.displayName = "AuthContext";

const COOKIE_MAX_AGE_IN_SECONDS = 90 * 120;
const TOKEN_COOKIE_NAME = "vlab.session.token";

//not used for any auth purpose, just convenience/debug
const SESSION_ID_COOKIE_NAME = "vlab.session.id";
const SESSION_ROLE_COOKIE_NAME = "vlab.session.role";

export const AuthProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
	const VLabApi = useContext(VLabAPIContext);
	const LogCtx = useContext(LogContext);
	const logger = useLogger("providers.AuthProvider");

	const [authenticated, setAuthenticated] = useState(false);
	const [token, setToken, tokenRef] = useStateRef<string>();
	const [lastExchangeToken, setLastExchangeToken, lastExchangeTokenRef] = useStateRef<string>();
	//not used for any auth purpose
	const [sessionID, setSessionID, sessionIDRef] = useStateRef<string>();
	const [authenticating, setAuthenticating, authenticatingRef] = useStateRef(false);
	const [role, setRole, roleRef] = useStateRef<VLabRole>();
	const [actor, setActor, actorRef] = useStateRef<Patient | Person>();
	const [patient, setPatient, patientRef] = useStateRef<Patient>();
	const [onFailureRedirectUri, setOnFailureRedirectUri, onFailureRedirectUriRef] = useStateRef<string>();
	const [onSuccessRedirectUri, setOnSuccessRedirectUri, onSuccessRedirectUriRef] = useStateRef<string>();
	const componentUnloaded = useRef(false);

	useEffect(() => {
		startup();
		componentUnloaded.current = false;
		return () => {
			componentUnloaded.current = true;
		};
	}, []);

	const startup = () => {
		setAuthenticating(true);
		const cookieToken = getCookie(TOKEN_COOKIE_NAME);
		const sessionIDToken = getCookie(SESSION_ID_COOKIE_NAME);
		if (cookieToken && sessionIDToken) {
			setToken(cookieToken);
			setSessionID(sessionIDToken);
			validateToken();
		} else {
			setAuthenticated(false);
			setPatient(undefined);
			setActor(undefined);
			setSessionID(undefined);
			setToken(undefined);
			setAuthenticating(false);
		}
	};

	const logout = () => {
		deleteCookie(TOKEN_COOKIE_NAME);
		deleteCookie(SESSION_ROLE_COOKIE_NAME);
		deleteCookie(SESSION_ID_COOKIE_NAME);
		setAuthenticated(false);
		setSessionID(undefined);
		setToken(undefined);
		setPatient(undefined);
		setActor(undefined);
		setAuthenticating(false);
		logger.info("logout", "Logged out.");
	};

	const validateToken = async () => {
		try {
			if (!tokenRef.current) {
				throw new Error("Expected tokenRef.current.");
			}
			const response = await VLabApi.call<any, ValidateLoginResponse>("GET", "/v1/auth/$validate", {
				headers: {
					"x-api-key": tokenRef.current
				}
			});
			if (componentUnloaded.current) {
				return;
			}
			if (response?.sessionID) {
				setSessionID(response.sessionID);
				setAuthenticated(true);
				setActor(response.actor);
				setPatient(response.patient);
				setOnFailureRedirectUri(response.onFailureRedirectUri);
				setOnSuccessRedirectUri(response.onSuccessRedirectUri);
				const VLAB_ROLE = response.roles?.find((item) => item?.identifier?.system === "emed.com/vlab/role")?.identifier
					?.value as VLabRole;
				logger.info("validateToken", "Validate login success", {
					role: VLAB_ROLE,
					sessionID: response.sessionID
				});
				setRole(VLAB_ROLE);
				LogCtx.updateUser({
					sessionID: response.sessionID,
					role: VLAB_ROLE,
					actor: response.actor
				});
			} else {
				setSessionID(undefined);
				setAuthenticated(false);
				setPatient(undefined);
				setActor(undefined);
			}

			setAuthenticated(true);
			setAuthenticating(false);
		} catch (e) {
			//TODO: redirect?
			logger.error("validateToken", "Unable to validate login. Logging out", e);
			logout();
		}
	};

	const setCredential = (cred: ExchangeCredentialResponse) => {
		if (cred?.token && cred?.sessionID) {
			setAuthenticated(true);
			setToken(cred.token);
			setSessionID(cred.sessionID);
			setActor(cred.actor);
			setPatient(cred.patient);
			setOnFailureRedirectUri(cred.onFailureRedirectUri);
			setOnSuccessRedirectUri(cred.onSuccessRedirectUri);
			setCookie(TOKEN_COOKIE_NAME, cred.token, {
				maxAge: COOKIE_MAX_AGE_IN_SECONDS
			});
			//not used for any auth purpose, just convenience/debug
			setCookie(SESSION_ID_COOKIE_NAME, cred.sessionID, {
				maxAge: COOKIE_MAX_AGE_IN_SECONDS
			});

			const VLAB_ROLE = cred?.roles?.find((item) => item?.identifier?.system === "emed.com/vlab/role")?.identifier
				?.value as VLabRole;
			logger.info("setCredential", "Login success", {
				role: VLAB_ROLE,
				sessionID: cred.sessionID
			});
			setRole(VLAB_ROLE);
			setCookie(SESSION_ROLE_COOKIE_NAME, VLAB_ROLE, {
				maxAge: COOKIE_MAX_AGE_IN_SECONDS
			});
			setAuthenticating(false);

			LogCtx.updateUser({
				sessionID: cred.sessionID,
				role: VLAB_ROLE,
				actor: cred.actor
			});

			return cred.token;
		} else {
			throw new Error("Expected token. Unable to login.");
		}
	};

	const loginWithExchangeToken = async (exchangeToken: string) => {
		try {
			if (authenticatingRef.current) return;
			logger.debug("loginWithExchangeToken", "loginWithExchangeToken called. Begin exchange credential.");
			setAuthenticating(true);
			logout();
			const response = await VLabApi.call<ExchangeCredentialRequest, ExchangeCredentialResponse>(
				"POST",
				"/v1/auth/$exchange-credential",
				{
					data: {
						exchangeToken
					}
				}
			);
			if (componentUnloaded.current) {
				logger.debug("loginWithExchangeToken", "Component unloaded. Ignoring loginWithExchangeToken response.");
				return;
			}
			setLastExchangeToken(exchangeToken);
			return setCredential(response);
		} catch (e) {
			setAuthenticating(false);
			const error = e as LoveClientError;
			logger.error("loginWithExchangeToken", e.message, e, { data: error?.response?.data });
			throw e;
		}
	};
	const value: AuthContext = {
		authenticated,
		authenticating,
		loginWithExchangeToken,
		setCredential,
		sessionID,
		role,
		logout,
		actor,
		patient,
		token,
		tokenRef,
		lastExchangeTokenRef,
		onFailureRedirectUri,
		onSuccessRedirectUri
	};
	return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};
export const useAuth = () => {
	return useContext(AuthContext);
};
